import { Box } from '@mui/material';
import { FormattedDateTimeRange, FormattedMessage } from 'react-intl';
import { Localized, PoolFileList } from '@synergy/components';
import { PoolLink } from '@synergy/components-next';
import { PropsWithChildren, ReactElement } from 'react';
import { UploadType } from '@synergy/models';
import { converter, getLocalizedString } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import Scrollspy from 'react-scrollspy';
import dayjs from 'dayjs';

import { DemandDetail } from '../models/demand-detail';
import { localesSelector } from '../../../../common/selectors/locale-selectors';
import { useSelector } from '../../../../store/use-appstate-selector';

const c = tokens.color;

const Entry = ({ label, children }: PropsWithChildren<{ label: ReactElement }>): ReactElement => (
    <Box sx={{ marginBottom: '15px' }}>
        <Box
            sx={{
                fontSize: '9.5px',
                fontWeight: 600,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: c.ink3,
                marginBottom: '3px',
            }}
        >
            {label}
        </Box>
        <Box sx={{ fontSize: '12.5px', color: c.ink, '& p': { margin: 0 } }}>{children}</Box>
    </Box>
);

/**
 * The content of the detail dock: what the demand is about, who to ask, and a
 * jump list over its articles.
 *
 * The legacy sidebar brought its own dark title bar, which read as neither the
 * old look nor PALO once the theme changed. The dock around this supplies the
 * heading now.
 */
export const PaloDemandsDetailDock = ({ demand }: { demand: DemandDetail }): ReactElement => {
    const mediaBaseUrl = useSelector(state => state.settings.mediaBaseUrl);
    const locales = useSelector(localesSelector);

    const noteToCustomer = demand.noteToCustomer && getLocalizedString(locales, demand.noteToCustomer);
    const customerMedia = demand.media.filter(file => file.type === UploadType.Customer);
    const anchors = [...demand.demandItems.map(item => `demand-${item.id}`), 'noteFromCustomer'];

    const jumpTo = (id: string): void => {
        document.getElementById(id)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };

    return (
        <Box>
            {demand.deliveryDateFrom && demand.deliveryDateTo && (
                <Entry label={<FormattedMessage id="app.demands.deliveryDateRange" />}>
                    <FormattedDateTimeRange
                        day="2-digit"
                        month="short"
                        year="numeric"
                        from={dayjs(demand.deliveryDateFrom).toDate()}
                        to={dayjs(demand.deliveryDateTo).toDate()}
                    />
                </Entry>
            )}

            <Entry label={<FormattedMessage id="app.common.responsibleContact" />}>
                <Box>{demand.responsibleContact?.fullName}</Box>
                {demand.responsibleContact?.email && (
                    <Box>
                        <PoolLink href={`mailto:${demand.responsibleContact.email}`} colorVariant="black">
                            {demand.responsibleContact.email}
                        </PoolLink>
                    </Box>
                )}
                {demand.responsibleContact?.phone && (
                    <Box>
                        <PoolLink href={`tel:${demand.responsibleContact.phone}`} colorVariant="black">
                            {demand.responsibleContact.phone}
                        </PoolLink>
                    </Box>
                )}
            </Entry>

            {noteToCustomer && (
                <Entry label={<FormattedMessage id="app.demands.detail.noteToCustomer" />}>
                    <Box dangerouslySetInnerHTML={{ __html: converter.makeHtml(noteToCustomer) }} />
                </Entry>
            )}

            {customerMedia.length > 0 && (
                <Entry label={<FormattedMessage id="app.demands.detail.media" />}>
                    <PoolFileList acceptedFiles={customerMedia} downloadBaseUrl={mediaBaseUrl} hideTitle={true} />
                </Entry>
            )}

            <Box
                sx={{
                    borderTop: `1px solid ${c.line}`,
                    marginTop: '4px',
                    paddingTop: '9px',
                    '& ul': { listStyle: 'none', margin: 0, padding: 0 },
                    '& li': {
                        fontSize: '12.5px',
                        color: c.ink2,
                        padding: '6px 8px',
                        borderRadius: tokens.radius.small + 'px',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: c.surface2, color: c.ink },
                    },
                    '& li.palo-jump-active': { backgroundColor: c.accentSoft, color: c.accentHi, fontWeight: 600 },
                }}
            >
                <Scrollspy items={anchors} currentClassName="palo-jump-active" rootEl="#scroll-container">
                    {demand.demandItems.map(item => (
                        <li key={item.id} onClick={() => jumpTo(`demand-${item.id}`)}>
                            <Localized value={item.name} />
                        </li>
                    ))}
                    <li onClick={() => jumpTo('noteFromCustomer')}>
                        <FormattedMessage id="app.common.note" />
                    </li>
                </Scrollspy>
            </Box>
        </Box>
    );
};
